import { useAuth } from '@/hooks/useAuth';
import { useT } from '@/i18n';
import { Button } from '@/components/ui';
import { Leaf, LogOut, User, ChevronRight, Settings, Sprout } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { NotificationBell } from '@/components/notifications';

interface Breadcrumb {
  label: string;
  href?: string;
}

interface HeaderProps {
  breadcrumbs?: Breadcrumb[];
}

export function Header({ breadcrumbs }: HeaderProps) {
  const { user, logout } = useAuth();
  const { t } = useT();
  const location = useLocation();

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'superadmin': return 'bg-red-100 text-red-800';
      case 'admin': return 'bg-purple-100 text-purple-800';
      case 'operator': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'superadmin': return t('role.superadmin');
      case 'admin': return t('role.admin');
      case 'operator': return t('role.operator');
      default: return t('role.viewer');
    }
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  return (
    <header className="bg-white dark:bg-gray-900 shadow-sm sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-4 py-3 header-safe-area">
        <div className="flex items-center justify-between">

          {/* Logo & Breadcrumbs */}
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center shadow-sm">
                <Leaf className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-gray-900 dark:text-gray-100 hidden sm:block">
                GreenHouse Pro
              </span>
            </Link>

            {breadcrumbs && breadcrumbs.length > 0 && (
              <nav className="hidden md:flex items-center gap-1 text-sm">
                <ChevronRight className="w-4 h-4 text-gray-400" />
                {breadcrumbs.map((crumb, index) => (
                  <div key={index} className="flex items-center gap-1">
                    {crumb.href ? (
                      <Link to={crumb.href} className="text-gray-500 hover:text-primary transition-colors">
                        {crumb.label}
                      </Link>
                    ) : (
                      <span className="text-gray-900 dark:text-gray-100 font-medium">{crumb.label}</span>
                    )}
                    {index < breadcrumbs.length - 1 && (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                ))}
              </nav>
            )}
          </div>

          {/* Navigation & User Menu */}
          <div className="flex items-center gap-2">

            <Link
              to="/agriculture"
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                location.pathname.startsWith('/agriculture')
                  ? 'text-primary bg-primary/10'
                  : 'text-gray-600 dark:text-gray-300 hover:text-primary hover:bg-primary/5'
              }`}
            >
              <Sprout className="w-4 h-4" />
              <span className="hidden sm:inline">{t('nav.agriculture')}</span>
            </Link>

            <NotificationBell />

            {isAdmin && (
              <Link
                to="/admin/users"
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  location.pathname.startsWith('/admin')
                    ? 'text-primary bg-primary/10'
                    : 'text-gray-600 dark:text-gray-300 hover:text-primary hover:bg-primary/5'
                }`}
              >
                <Settings className="w-4 h-4" />
                <span className="hidden sm:inline">{t('nav.admin')}</span>
              </Link>
            )}

            <div className="hidden sm:flex items-center gap-2 ml-2 pl-2 border-l border-gray-200 dark:border-gray-700">
              <Link to="/profile" className="flex items-center gap-2 hover:opacity-80">
                <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                  <User className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{user?.username}</div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${getRoleBadgeColor(user?.role || '')}`}>
                    {getRoleLabel(user?.role || '')}
                  </span>
                </div>
              </Link>
            </div>

            <Link
              to="/profile"
              className="sm:hidden inline-flex items-center justify-center w-9 h-9 rounded-lg text-gray-600 dark:text-gray-300 hover:text-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <User className="w-4 h-4" />
            </Link>

            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="text-gray-600 dark:text-gray-300 hover:text-gray-900"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">{t('nav.logout')}</span>
            </Button>

          </div>
        </div>
      </div>
    </header>
  );
}